/* eslint-disable @typescript-eslint/no-non-null-assertion,@typescript-eslint/consistent-type-assertions */
import type { PageAction } from "@octopusdeploy/design-system-components";
import { Callout } from "@octopusdeploy/design-system-components";
import { StringHelper } from "@octopusdeploy/design-system-octopus-components";
import type { CertificateResource, EnvironmentV2Resource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { CertificateDataFormat, getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled, Permission, TenantedDeploymentMode } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as _ from "lodash";
import * as React from "react";
import type { ActionEvent, AnalyticTrackedActionDispatcher } from "~/analytics/Analytics";
import { Action, useAnalyticTrackedActionDispatch, useCorrelationId } from "~/analytics/Analytics";
import type { VersionControlledProjectUsage } from "~/areas/infrastructure/components/VersionControlledProjectUsages/VersionControlledProjectUsages";
import { getVersionControlledProjectUsagesIfFeatureToggleIsEnabled, VersionControlledProjectUsages } from "~/areas/infrastructure/components/VersionControlledProjectUsages/VersionControlledProjectUsages";
import ArchiveCertificate, { ArchiveAction } from "~/areas/library/components/Certificates/Certificate/ArchiveCertificate";
import { CertificateDetailsFormSections } from "~/areas/library/components/Certificates/Certificate/CertificateDetailsFormSections";
import CertificateUsage from "~/areas/library/components/Certificates/Certificate/CertificateUsage";
import DownloadCertificate from "~/areas/library/components/Certificates/Certificate/DownloadCertificate";
import { ExistingCertificateDetailsFormSections } from "~/areas/library/components/Certificates/Certificate/ExistingCertificateDetailsFormSections/ExistingCertificateDetailsFormSections";
import { NewCertificateDetailsFormSections } from "~/areas/library/components/Certificates/Certificate/NewCertificateDetailsFormSections";
import ReplaceCertificate from "~/areas/library/components/Certificates/Certificate/ReplaceCertificate";
import { certificateUsageSummary } from "~/areas/library/components/Certificates/Certificate/certificateUsageSummary";
import type { CertificateUsageEntry } from "~/areas/library/components/Certificates/Certificate/certificateUsageSummary";
import { useEphemeralEnvironmentsEnabled } from "~/areas/projects/components/EphemeralEnvironments/useEphemeralEnvironmentsEnabled";
import { repository } from "~/clientInstance";
import type { DoBusyTask, Errors } from "~/components/DataBaseComponent/index";
import type { OptionalFormBaseComponentState } from "~/components/FormBaseComponent/FormBaseComponent";
import { OptionalFormBaseComponent } from "~/components/FormBaseComponent/FormBaseComponent";
import { LegacyForm } from "~/components/FormPaperLayout/LegacyForm";
import InternalRedirect from "~/components/Navigation/InternalRedirect/InternalRedirect";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import type { ConvertedOverflowMenuItems } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import ThumbprintText from "~/components/ThumbprintText/index";
import TransitionAnimation from "~/components/TransitionAnimation/TransitionAnimation";
import { CardFill } from "~/components/form/Sections/ExpandableFormSection";
import { ExpandableFormSection, Summary } from "~/components/form/index";
import type { SummaryNode } from "~/components/form/index";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { TabItem, UrlNavigationTabsContainer } from "~/primitiveComponents/navigation/Tabs/index";
import { TenantsOrTenantTagsSelectedOnUntenantedDeploymentMode } from "~/utils/TenantedDeploymentParticipationHelper/TenantsOrTenantTagsSelectedOnUntenantedDeploymentMode";
interface CertificateProps {
    spaceId: string;
    create?: boolean;
    certificateId?: string;
    defaultTab?: string;
}
export type CertificatePropsInternal = FormStateChanged & {
    create?: boolean;
    certificateId?: string;
    defaultTab?: string;
    activeTabUrlParamName?: string;
    onStateTransition?: (currentState: CertificateStateTypes | null, newState: CertificateStateTypes) => void;
    expandableContainerKey?: string;
    trackAction: AnalyticTrackedActionDispatcher;
    correlationId?: string;
    ephemeralEnvironmentsAreEnabled: boolean;
    improveConfigAsCodeProjectUsagesEnabled: boolean;
    tenantId?: string;
};
interface CertificateState extends OptionalFormBaseComponentState<CertificateResource> {
    deleted: boolean;
    newId: string;
    allEnvironments: EnvironmentV2Resource[];
    allTenants: TenantResource[];
    certificateUsages: CertificateUsageEntry[];
    replacedByCertificate: CertificateResource;
    uploadFormat: UploadFormatOptions;
    versionControlledProjects: VersionControlledProjectUsage[];
}
interface FormState {
    model: CertificateResource | undefined;
    cleanModel: CertificateResource | undefined;
    busy: Promise<void> | undefined;
    errors?: Errors;
}
export interface FormStateChanged {
    onFormStateChanged: (newState: FormState) => void;
}
type UploadFormatOptions = "File" | "Text" | "SelfSigned";
interface CommonStateTransitionProps {
    primaryAction?: PageAction;
    secondaryActions?: PageAction[];
    overflowMenuItems?: ConvertedOverflowMenuItems;
}
export type CertificateCreateState = CommonStateTransitionProps & {
    state: "create";
};
export type CertificateNewState = CommonStateTransitionProps & {
    state: "new";
    certificateId: string;
    certificate: CertificateResource | undefined;
};
export type CertificateEditState = CommonStateTransitionProps & {
    state: "edit";
    certificateId: string;
    certificate: CertificateResource | undefined;
};
export type CertificateDeleteState = CommonStateTransitionProps & {
    state: "delete";
};
export type CertificateStateTypes = CertificateCreateState | CertificateNewState | CertificateEditState | CertificateDeleteState;
export function SharedCertificateForm(props: CertificateProps) {
    const [state, setState] = React.useState<CertificateStateTypes>({
        state: "create",
    });
    const [formState, setFormState] = React.useState<FormState>({
        busy: undefined,
        model: undefined,
        errors: undefined,
        cleanModel: undefined,
    });
    const [primaryAction, setPrimaryAction] = React.useState<PageAction>();
    const [secondaryActions, setSecondaryActions] = React.useState<PageAction[]>();
    const [overflowMenuItems, setOverflowMenuItems] = React.useState<ConvertedOverflowMenuItems>();
    const correlationId = useCorrelationId();
    const trackAction = useAnalyticTrackedActionDispatch();
    const ephemeralEnvironmentsAreEnabled = useEphemeralEnvironmentsEnabled();
    const improveConfigAsCodeProjectUsagesEnabled = useOctopusFeatureToggle("improve-config-as-code-project-usages", false);
    const onStateTransition = (currentState: CertificateStateTypes | null, newState: CertificateStateTypes) => {
        setState(newState);
        setPrimaryAction(newState.primaryAction);
        setSecondaryActions(newState.secondaryActions);
        setOverflowMenuItems(newState.overflowMenuItems);
    };
    if (state.state === "delete") {
        return <InternalRedirect to={links.certificatesPage.generateUrl({ spaceId: props.spaceId })}/>;
    }
    if (state.state === "new") {
        return <InternalRedirect to={links.certificatePage.generateUrl({ spaceId: props.spaceId, certificateId: state.certificateId })}/>;
    }
    const saveText: string = state.state ? "Certificate created" : "Certificate details updated";
    const title = state.state === "create" ? "Add Certificate" : formState.model ? formState.model.Name : StringHelper.ellipsis;
    return (<LegacyForm model={formState.model} cleanModel={formState.cleanModel} savePermission={{ permission: state.state === "create" ? Permission.CertificateCreate : Permission.CertificateEdit, wildcard: true }} onSaveClick={() => {
            if (primaryAction?.type === "button")
                primaryAction.onClick(undefined);
        }} saveText={saveText}>
            {({ FormContent, createSaveAction }) => (<PageContent header={{
                primaryAction: createSaveAction({}),
                title,
                breadcrumbs: [{ label: "Certificates", pageUrl: links.certificatesPage.generateUrl({ spaceId: props.spaceId }) }],
                pageActions: secondaryActions,
                overflowActions: overflowMenuItems && overflowMenuItems.menuItems,
            }} busy={formState.busy} errors={formState.errors}>
                    {overflowMenuItems && overflowMenuItems.dialogs}
                    <FormContent expandAllOnMount={props.create}>
                        <CertificateInternal trackAction={trackAction} certificateId={props.certificateId} onFormStateChanged={setFormState} onStateTransition={onStateTransition} create={props.create} defaultTab={props.defaultTab} correlationId={correlationId} ephemeralEnvironmentsAreEnabled={ephemeralEnvironmentsAreEnabled} improveConfigAsCodeProjectUsagesEnabled={improveConfigAsCodeProjectUsagesEnabled}/>
                    </FormContent>
                </PageContent>)}
        </LegacyForm>);
}
export class CertificateInternal extends OptionalFormBaseComponent<CertificatePropsInternal, CertificateState, CertificateResource> {
    constructor(props: CertificatePropsInternal) {
        super(props);
        this.state = {
            deleted: false,
            newId: null!,
            allEnvironments: [],
            allTenants: [],
            certificateUsages: null!,
            replacedByCertificate: null!,
            uploadFormat: "File",
            versionControlledProjects: [],
        };
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            let certificate: CertificateResource = null!;
            let replacedByCertificate: CertificateResource = null!;
            if (this.props.create) {
                certificate = {
                    Id: null!,
                    Name: "",
                    Notes: "",
                    CertificateData: { NewValue: null!, HasValue: false },
                    Password: { NewValue: null!, HasValue: false },
                    EnvironmentIds: [],
                    TenantIds: this.props.tenantId ? [this.props.tenantId] : [],
                    TenantTags: [],
                    TenantedDeploymentParticipation: this.props.tenantId ? TenantedDeploymentMode.Tenanted : TenantedDeploymentMode.Untenanted,
                    Links: null!,
                    SpaceId: null!,
                };
            }
            else {
                certificate = await repository.Certificates.get(this.props.certificateId!);
                if (certificate.ReplacedBy) {
                    replacedByCertificate = await repository.Certificates.get(certificate.ReplacedBy);
                }
            }
            const tenantsPromise = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all() : Promise.resolve([]);
            const environments = await getStaticAndParentEnvironmentsDependingOnIfEphemeralEnvironmentsAreEnabled(this.props.ephemeralEnvironmentsAreEnabled, repository);
            this.setState({
                model: certificate,
                cleanModel: _.cloneDeep(certificate),
                allEnvironments: environments,
                allTenants: await tenantsPromise,
                replacedByCertificate,
            });
        });
    }
    componentDidUpdate(prevProps: CertificatePropsInternal, prevState: CertificateState) {
        if (prevState.busy !== this.state.busy || prevState.model !== this.state.model || prevState.cleanModel !== this.state.cleanModel || prevState.deleted !== this.state.deleted) {
            this.notifyFormStateChanged();
        }
    }
    private notifyFormStateChanged() {
        this.props.onFormStateChanged({
            busy: this.state.busy,
            model: this.state.model,
            cleanModel: this.state.cleanModel,
            errors: this.errors,
        });
        const overFlowActions = [];
        if (this.state.model && !this.props.create) {
            const download = <DownloadCertificate certificate={this.state.model}/>;
            overFlowActions.push(OverflowMenuItems.dialogItem("Download", download));
            if (!this.state.model.Archived && !this.state.model.ReplacedBy) {
                const replace = <ReplaceCertificate certificate={this.state.model} afterCertificateReplace={(c) => this.handleCertificateReplaced(c)}/>;
                overFlowActions.push(OverflowMenuItems.dialogItem("Replace", replace, { permission: Permission.CertificateEdit, wildcard: true }));
            }
            if (this.state.model.Archived) {
                overFlowActions.push(OverflowMenuItems.deleteItemDefault("certificate", this.handleDeleteConfirm, { permission: Permission.CertificateDelete, wildcard: true }));
                if (!this.state.model.ReplacedBy) {
                    const unarchive = <ArchiveCertificate certificate={this.state.model} action={ArchiveAction.Unachive} afterAction={() => this.handleArchive()}/>;
                    overFlowActions.push(OverflowMenuItems.dialogItem("Unarchive", unarchive, { permission: Permission.CertificateEdit, wildcard: true }));
                }
            }
            else {
                const archive = <ArchiveCertificate certificate={this.state.model} action={ArchiveAction.Archive} afterAction={() => this.handleArchive()}/>;
                overFlowActions.push(OverflowMenuItems.dialogItem("Archive", archive, { permission: Permission.CertificateEdit, wildcard: true }));
            }
            overFlowActions.push([
                OverflowMenuItems.navItem("Audit Trail", links.auditPage.generateUrl({ regardingAny: [this.state.model.Id] }), {
                    permission: Permission.EventView,
                    wildcard: true,
                }),
            ]);
        }
        const commonProps: CommonStateTransitionProps = {
            primaryAction: {
                type: "button",
                buttonType: "secondary",
                hasPermissions: isAllowed({ permission: Permission.CertificateEdit }),
                label: "Save",
                busyLabel: "Saving...",
                onClick: () => {
                    this.handleSaveClick();
                },
            },
            overflowMenuItems: OverflowMenuConverterVNext.convertAll(overFlowActions),
        };
        if (this.props.create && !this.state.newId) {
            this.props.onStateTransition?.(null, {
                state: "create",
                ...commonProps,
            });
        }
        else {
            const state = this.state.newId ? "new" : null;
            if (this.state.deleted) {
                this.props.onStateTransition?.(null, {
                    state: "delete",
                    ...commonProps,
                });
            }
            else if (state !== null) {
                this.props.onStateTransition?.(null, {
                    state: state,
                    certificateId: this.state.newId ? this.state.newId : this.state.model!.Id,
                    certificate: this.state.model,
                    ...commonProps,
                });
            }
            else if (this.state.model) {
                this.props.onStateTransition?.(null, {
                    state: "edit",
                    certificateId: this.state.newId ? this.state.newId : this.state.model!.Id,
                    certificate: this.state.model,
                    ...commonProps,
                });
            }
        }
    }
    render() {
        return (<>
                {this.state.model && (<CertificateFormInternal certificateUsages={this.state.certificateUsages} model={this.state.model} uploadFormat={this.state.uploadFormat} getFieldError={this.getFieldError} create={this.props.create} replacedByCertificate={this.state.replacedByCertificate} setModelState={(state) => {
                    this.setModelState(state);
                }} doBusyTask={this.doBusyTask} allEnvironments={this.state.allEnvironments} allTenants={this.state.allTenants} defaultTab={this.props.defaultTab} activeTabUrlParamName={this.props.activeTabUrlParamName} setUploadFormat={(uploadFormat) => this.setState({ uploadFormat }, () => this.setModelState({ CertificateData: { HasValue: true } }))} setCertificateUsages={(certificateUsages, versionControlledProjects) => this.setState({ certificateUsages, versionControlledProjects })} improveConfigAsCodeProjectUsagesEnabled={this.props.improveConfigAsCodeProjectUsagesEnabled} versionControlledProjects={this.state.versionControlledProjects}/>)}
            </>);
    }
    private handleSaveClick = async () => {
        if (this.state.model && TenantsOrTenantTagsSelectedOnUntenantedDeploymentMode(this.state.model)) {
            this.setValidationErrors("Tenanted deployment mode", { TenantedDeploymentParticipation: "Please remove any associated tenants or tenant tags to use Untenanted deployment mode." });
            return;
        }
        await this.doBusyTask(async () => {
            const isNew = this.state.model!.Id === null;
            const ev: ActionEvent = {
                action: Action.Save,
                resource: "Certificate",
                location: "Page",
                entityType: isNew ? "New" : "Existing",
                correlationId: this.props.correlationId,
            };
            await this.props.trackAction("Save Certificate", ev, async () => {
                const certificate = this.state.uploadFormat === "SelfSigned" ? await repository.Certificates.saveSelfSigned(this.state.model!) : await repository.Certificates.save(this.state.model!);
                this.setState({
                    model: certificate,
                    cleanModel: _.cloneDeep(certificate),
                    newId: isNew ? certificate.Id : null!,
                });
            });
        });
    };
    private handleDeleteConfirm = async () => {
        const result = await repository.Certificates.del(this.state.model!);
        this.setState((state) => {
            return {
                model: null,
                cleanModel: null,
                deleted: true,
            };
        });
        return true;
    };
    private handleCertificateReplaced(replacedCertificate: CertificateResource) {
        const cert = this.state.model;
        this.setState({
            model: replacedCertificate!,
            cleanModel: _.cloneDeep(replacedCertificate),
            newId: replacedCertificate.Id,
            replacedByCertificate: cert!,
        });
    }
    private handleArchive = async () => {
        await this.doBusyTask(async () => {
            const certificate = await repository.Certificates.get(this.props.certificateId!);
            let replacedByCertificate: CertificateResource | null = null;
            if (certificate.ReplacedBy) {
                replacedByCertificate = await repository.Certificates.get(certificate.ReplacedBy);
            }
            this.setState(() => ({
                model: certificate!,
                cleanModel: _.cloneDeep(certificate),
                certificateUsages: null,
                replacedByCertificate,
            }));
        });
    };
}
interface CertificateFormInternalProps {
    create?: boolean;
    model: CertificateResource;
    certificateUsages: CertificateUsageEntry[] | null;
    versionControlledProjects: VersionControlledProjectUsage[];
    allEnvironments: EnvironmentV2Resource[];
    allTenants: TenantResource[];
    doBusyTask: DoBusyTask;
    defaultTab?: string;
    activeTabUrlParamName?: string;
    replacedByCertificate?: CertificateResource;
    getFieldError: (fieldName: string) => string;
    setModelState: <K extends keyof CertificateResource>(state: Pick<CertificateResource, K>) => void;
    setCertificateUsages: (certificateUsages: CertificateUsageEntry[], versionControlledProjects: VersionControlledProjectUsage[]) => void;
    setUploadFormat: (uploadFormat: UploadFormatOptions) => void;
    uploadFormat: UploadFormatOptions;
    improveConfigAsCodeProjectUsagesEnabled: boolean;
}
const CertificateFormInternal = (props: CertificateFormInternalProps) => {
    const usageSummary = (): SummaryNode => {
        return props.certificateUsages && props.certificateUsages.length > 0
            ? props.certificateUsages.length > 1
                ? Summary.summary(<span>
                          This certificate is used in <b>{props.certificateUsages.length}</b> places
                      </span>)
                : Summary.summary(<span>
                          This certificate is used in <b>one</b> place
                      </span>)
            : Summary.placeholder("This certificate is not used anywhere");
    };
    const onUsageTabActive = async () => {
        if (props.certificateUsages || props.create) {
            return;
        }
        await props.doBusyTask(async () => {
            const certificateUsageData = await repository.CertificateConfiguration.usage(props.model);
            const certificateUsages = certificateUsageSummary(certificateUsageData);
            const versionControlledProjects = await getVersionControlledProjectUsagesIfFeatureToggleIsEnabled(props.improveConfigAsCodeProjectUsagesEnabled, repository);
            props.setCertificateUsages(certificateUsages, versionControlledProjects);
        });
    };
    const certificateUsageInVersionControlledProjectsSummary = (versionControlledProjects: VersionControlledProjectUsage[]) => {
        return versionControlledProjects.length > 0
            ? versionControlledProjects.length > 1
                ? Summary.summary(<span>
                          This certificate may be used in <b>{versionControlledProjects.length}</b> version controlled projects
                      </span>)
                : Summary.summary(<span>
                          This certificate may be used in <b>1</b> version controlled project
                      </span>)
            : Summary.placeholder("This certificate is not being used in any version controlled projects");
    };
    const certificateUsageInVersionControlledProjectsHelp = (versionControlledProjects: VersionControlledProjectUsage[]) => {
        return versionControlledProjects.length === 0
            ? "This certificate is not being used in any version controlled projects"
            : versionControlledProjects.length === 1
                ? "This certificate may be used in the following version controlled project"
                : "This certificate may be used in the following version controlled projects";
    };
    return (<TransitionAnimation>
            <UrlNavigationTabsContainer defaultValue={props.defaultTab ?? "details"} urlParameterName={props.activeTabUrlParamName}>
                <TabItem label="Details" value="details">
                    {props.replacedByCertificate && (<Callout title="Replaced" type={"information"}>
                            This certificate was replaced by certificate with thumbprint <ThumbprintText thumbprint={props.replacedByCertificate.Thumbprint!}/>
                        </Callout>)}
                    {props.model.Archived && (<Callout title="Archived" type={"information"}>
                            This certificate was archived on {props.model.Archived}
                        </Callout>)}
                    {props.model.CertificateDataFormat === CertificateDataFormat.Unknown && (<Callout title="Invalid Certificate" type={"warning"}>
                            This certificate was unable to be parsed and may be in an invalid format. This certificate will not be able to be used in Octopus deployments and you may need to upload a new certificate which can be correctly loaded.
                        </Callout>)}
                    <CertificateDetailsFormSections model={props.model} setModelState={props.setModelState} getFieldError={props.getFieldError} allTenants={props.allTenants} allEnvironments={props.allEnvironments} doBusyTask={props.doBusyTask} certificateDetails={<>
                                {!props.create && props.model.CertificateDataFormat !== CertificateDataFormat.Unknown && <ExistingCertificateDetailsFormSections model={props.model}/>}
                                {props.create && props.model.CertificateDataFormat !== CertificateDataFormat.Unknown && (<NewCertificateDetailsFormSections model={props.model} setModelState={props.setModelState} uploadFormat={props.uploadFormat} setUploadFormat={props.setUploadFormat} getFieldError={props.getFieldError}/>)}
                            </>}/>
                </TabItem>
                {!props.create && (<TabItem label="Usage" value="usage" onActive={() => onUsageTabActive()}>
                        <ExpandableFormSection errorKey="Usage" title="Usage" summary={usageSummary()} help="This certificate can be referenced by variables">
                            <CertificateUsage certificateUsage={props.certificateUsages ?? []}/>
                        </ExpandableFormSection>
                        {props.improveConfigAsCodeProjectUsagesEnabled && (<ExpandableFormSection errorKey="VersionControlledProjects" title="Version Controlled Projects" summary={certificateUsageInVersionControlledProjectsSummary(props.versionControlledProjects)} help={certificateUsageInVersionControlledProjectsHelp(props.versionControlledProjects)} fillCardWidth={CardFill.FillRight}>
                                <Callout type="information" title="Usages in version controlled projects">
                                    <p>Deployment processes, runbooks and variables are stored in Git repositories for version controlled projects. Usages above for projects do not include Git repositories for performance reasons.</p>
                                    <p>
                                        Please check <code>.ocl</code> files for the certificate name <code>{props.model.Name}</code> in the Git repository for the following version controlled projects before deleting this certificate.
                                    </p>
                                </Callout>
                                <VersionControlledProjectUsages versionControlledProjects={props.versionControlledProjects}/>
                            </ExpandableFormSection>)}
                    </TabItem>)}
            </UrlNavigationTabsContainer>
        </TransitionAnimation>);
};
